import { FC, RefCallback, useCallback, useLayoutEffect, useMemo, useRef, useState, CSSProperties } from 'react';
import classnames from 'classnames';

import { useWhenFontLoaded } from '@hh.ru/magritte-common-use-when-font-loaded';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { TableContext } from '@hh.ru/magritte-ui-table/context';
import { TableProps } from '@hh.ru/magritte-ui-table/types';
import { useTableResize } from '@hh.ru/magritte-ui-table/useTableResize';

import cellStyles from './cell.less';
import styles from './table.less';

const DENSITY_TO_CSS = {
    'extra-small': styles.densityExtraSmall,
    small: styles.densitySmall,
    medium: styles.densityMedium,
    large: styles.densityLarge,
};

export const Table: FC<TableProps> = ({
    density = 'small',
    topPanel,
    bottomPanel,
    fixedSize,
    stickyBottom,
    stickyHeader,
    divider,
    dividerTop,
    dividerBottom,
    tinted,
    style = 'primary',
    styleHighlighted,
    tintedStyle = 'secondary',
    tintedStyleHighlighted,
    topStyle,
    bottomStyle,
    padding,
    paddingLeft: _paddingLeft,
    paddingRight: _paddingRight,
    cellsGap,
    onScrollHorizontal,
    'data-qa': dataQa = 'table-wrapper',
    children,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const scrollbarContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollbarRef = useRef<HTMLDivElement | null>(null);
    const [headerNode, setHeaderNode] = useState<HTMLDivElement | null>(null);
    const topWithDivider = dividerTop ?? !!divider;
    const bottomWithDivider = dividerBottom ?? !!divider;
    const paddingLeft = _paddingLeft ?? padding;
    const paddingRight = _paddingRight ?? padding;
    const highlightedColor = styleHighlighted || style;
    const tintedHighlightedColor = tintedStyleHighlighted || tintedStyle;
    const topColor = topStyle || style;
    const bottomColor = bottomStyle || style;
    const [fontLoaded, setFontLoaded] = useState(false);
    const { isMobile } = useBreakpoint();
    useWhenFontLoaded(() => setFontLoaded(true));
    const { subscribeResizeHandler, unsubscribeResizeHandler } = useTableResize({
        tableRef,
        fontLoaded,
    });

    const value = useMemo(
        () => ({
            headerNode,
            stickyHeader,
            paddingLeft,
            paddingRight,
            fixedSize,
            fontLoaded,
            divider: !!divider,
            subscribeResizeHandler,
            unsubscribeResizeHandler,
        }),
        [
            headerNode,
            stickyHeader,
            paddingLeft,
            paddingRight,
            fixedSize,
            fontLoaded,
            divider,
            subscribeResizeHandler,
            unsubscribeResizeHandler,
        ]
    );

    const setScrollStyles = useCallback(() => {
        if (wrapperRef.current && contentRef.current) {
            wrapperRef.current.classList.toggle(cellStyles.hasScrollLeft, contentRef.current.scrollLeft > 0);
            wrapperRef.current.classList.toggle(
                cellStyles.hasScrollRight,
                Math.round(contentRef.current.scrollLeft) + contentRef.current.offsetWidth + (paddingRight ?? 0) <
                    contentRef.current.scrollWidth
            );
        }
    }, [paddingRight]);

    const setFakeScrollbar = useCallback(() => {
        if (scrollbarRef.current && tableRef.current && contentRef.current && scrollbarContainerRef.current) {
            if (contentRef.current.scrollWidth > contentRef.current.offsetWidth) {
                scrollbarRef.current.style.setProperty('width', `${tableRef.current.getBoundingClientRect().width}px`);
                scrollbarContainerRef.current.style.setProperty('display', 'block');
            } else {
                scrollbarRef.current.style.setProperty('width', 'auto');
                scrollbarContainerRef.current.style.setProperty('display', 'none');
            }
        }
    }, []);

    const calcDividersAndScrollbar = useCallback(() => {
        setScrollStyles();
        setFakeScrollbar();
    }, [setScrollStyles, setFakeScrollbar]);

    const handleScrollContent = useCallback(() => {
        if (headerRef.current) {
            headerRef.current.scrollLeft = contentRef.current?.scrollLeft || 0;
        }
        if (scrollbarContainerRef.current) {
            scrollbarContainerRef.current.scrollLeft = contentRef.current?.scrollLeft || 0;
        }
        setScrollStyles();
        onScrollHorizontal?.();
    }, [setScrollStyles, onScrollHorizontal]);

    const handleHeaderScroll = useCallback(() => {
        if (contentRef.current) {
            contentRef.current.scrollLeft = headerRef.current?.scrollLeft || 0;
        }
        if (scrollbarContainerRef.current) {
            scrollbarContainerRef.current.scrollLeft = headerRef.current?.scrollLeft || 0;
        }
    }, []);

    const handleScrollContainerScroll = useCallback(() => {
        if (contentRef.current) {
            contentRef.current.scrollLeft = scrollbarContainerRef.current?.scrollLeft || 0;
        }

        if (headerRef.current) {
            headerRef.current.scrollLeft = scrollbarContainerRef.current?.scrollLeft || 0;
        }
    }, []);

    const headerRefCallback: RefCallback<HTMLDivElement> = useCallback((node) => {
        if (node) {
            setHeaderNode(node);
            headerRef.current = node;
        } else {
            headerRef.current = null;
        }
    }, []);

    useLayoutEffect(() => {
        if (!isMobile) {
            calcDividersAndScrollbar();
            subscribeResizeHandler?.(calcDividersAndScrollbar);

            return () => unsubscribeResizeHandler?.(calcDividersAndScrollbar);
        }
        return void 0;
    }, [calcDividersAndScrollbar, isMobile, subscribeResizeHandler, unsubscribeResizeHandler]);

    if (isMobile) {
        return null;
    }

    return (
        <div
            className={classnames(styles.wrapper, DENSITY_TO_CSS[density], {
                [styles.fixedSize]: fixedSize,
                [styles[`style-${style}`]]: style,
                [styles[`style-highlighted-${highlightedColor}`]]: highlightedColor,
                [styles[`tinted-style-${tintedStyle}`]]: tinted && tintedStyle,
                [styles[`tinted-style-highlighted-${tintedHighlightedColor}`]]: tinted && tintedHighlightedColor,
                [styles[`top-style-${topColor}`]]: topColor,
                [styles[`bottom-style-${bottomColor}`]]: bottomColor,
            })}
            data-qa={dataQa}
            data-magritte-table-wrapper
            ref={wrapperRef}
            style={
                {
                    '--magritte-table-padding-left': `${paddingLeft ?? 0}px`,
                    '--magritte-table-padding-right': `${paddingRight ?? 0}px`,
                    ...(cellsGap ? { '--magritte-table-cell-padding': `${cellsGap / 2}px` } : {}),
                } as CSSProperties
            }
        >
            {(topPanel || stickyHeader) && (
                <div
                    className={classnames(styles.topContainer, {
                        [styles.sticky]: stickyHeader,
                    })}
                >
                    {topPanel && (
                        <div
                            className={classnames(styles.topPanel, {
                                [styles.withDivider]: topWithDivider,
                            })}
                            data-qa="table-top-panel"
                            style={{ paddingLeft, paddingRight }}
                        >
                            {topPanel}
                        </div>
                    )}
                    {stickyHeader && (
                        <div
                            ref={headerRefCallback}
                            onScroll={handleHeaderScroll}
                            data-qa="table-sticky-header-root"
                            className={classnames(styles.headerContainer)}
                        />
                    )}
                </div>
            )}
            <TableContext.Provider value={value}>
                <div
                    className={classnames(styles.container)}
                    ref={contentRef}
                    onScroll={handleScrollContent}
                    data-qa={'table-container'}
                >
                    <table
                        className={classnames(styles.table, {
                            [styles.divider]: !!divider,
                        })}
                        data-qa="table"
                        ref={tableRef}
                    >
                        {children}
                    </table>
                </div>
            </TableContext.Provider>
            {(!bottomPanel || !stickyBottom) && (
                <div className={classnames(styles.bottomContainer, styles.sticky)}>
                    <div
                        className={styles.scrollContainer}
                        onScroll={handleScrollContainerScroll}
                        ref={scrollbarContainerRef}
                    >
                        <div className={styles.scrollContent} ref={scrollbarRef}></div>
                    </div>
                </div>
            )}
            {bottomPanel && (
                <div
                    className={classnames(styles.bottomContainer, {
                        [styles.sticky]: stickyBottom,
                    })}
                >
                    {stickyBottom && (
                        <div
                            className={styles.scrollContainer}
                            onScroll={handleScrollContainerScroll}
                            ref={scrollbarContainerRef}
                        >
                            <div className={styles.scrollContent} ref={scrollbarRef}></div>
                        </div>
                    )}
                    {bottomPanel && (
                        <div
                            className={classnames(styles.bottomPanel, {
                                [styles.withDivider]: bottomWithDivider,
                            })}
                            data-qa="table-bottom-panel"
                            style={{ paddingLeft, paddingRight }}
                        >
                            {bottomPanel}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
